import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core'
import { Events, AlertController, Loading, ToastController } from 'ionic-angular'
import { DeviceProvider } from '../../providers/device/device'
import { ConfigProvider } from '../../providers/config/config'
import { NativeAudio } from '@ionic-native/native-audio'
import { DomainEvents, CallStatus, HousingUnit } from '../../models/models'
import { AuthProvider } from '../../providers/auth/auth'
import { WebrtcProvider } from '../../providers/webrtc/webrtc'
import { SignalingSocketIoProvider } from '../../providers/signaling/socket_io'

@Component({
	selector: 'call',
	templateUrl: 'call.html'
})
export class CallComponent {
	/**
	E' connesso e ssta ricevendo audio e/o video
	*/
	public get isReceiving(): boolean {
		return this.callStatus == 'CONNECTED' && (this.isIncomingAudio || this.isIncomingVideo)
	}

	/**
	Non ha terminato la connessione ma non sta ricevendo stream
	*/
	public get isWaiting(): boolean {
		return this.callStatus != 'DISCONNECTED' && !this.isReceiving
	}

	public get isCalled(): boolean {
		return !this._isCaller
	}
	public get isCaller(): boolean {
		return this._isCaller
	}
	/**
	Se true agisce come chiamante, altrimenti come chiamato
	*/
	@Input() public set isCaller(isCaller: boolean) {
		console.debug('Imposto isCaller: ' + isCaller)
		this._isCaller = isCaller
		this.init()
	}

	/**
	Id unità abitativa per la quale parte la chiamata
	*/
	@Input() public housingUnit: HousingUnit = null;


	/**
	Abilita l'invio di video
	*/
	@Input() public isVideoSent: boolean = true;


	/**
	Id del peer remoto
	*/
	@Input() public set otherId(otherId: string) {
		console.debug('Imposto otherId: ' + otherId)
		this._otherId = otherId
		this.init()
	}
	public get otherId(): string {
		return this._otherId
	}

	/**
	Invocato quando riceve call init o call handled
	*/
	@Output() onConnected: EventEmitter<void> = new EventEmitter<void>()
	/**
	Invocato quando la connessione viene chiusa, specificando eventualmente il messaggio di chiusura
	*/
	@Output() onClose: EventEmitter<string> = new EventEmitter<string>()

	public outputStream: MediaStream = null;

	private _isCaller: boolean = null;
	private _otherId: string = null;

	@ViewChild('audioIn') inputAudioElement: ElementRef
	@ViewChild('videoIn') inputVideoElement: ElementRef
	@ViewChild('audioOut') outputAudioElement: ElementRef
	@ViewChild('videoOut') outputVideoElement: ElementRef


	/**
	Stato chiamata
	*/
	private _callStatus: CallStatus = CallStatus.NEW;
	public get callStatus(): CallStatus {
		return this._callStatus
	};
	public set callStatus(value: CallStatus) {
		this._callStatus = value
		this.forceChangeDetection()
	}



	private callTimeout: number = null;


	/**
	Se ha fatto hangUp, questo è il messaggio passato
	*/
	public hangUpMessage: string = null;

	/**
	Se true ha ricevuto CALL_INIT o CALL_HANDLED
	*/
	private _isConnected: boolean = false;
	public get isConnected(): boolean {
		return this._isConnected
	};
	public set isConnected(value: boolean) {
		this._isConnected = value
		this.forceChangeDetection()
	}

	/**
	Se true la chiamata e relativa connessione sono finalizzate
	*/
	private _isClosed: boolean = false;
	public get isClosed(): boolean {
		return this._isClosed
	};
	public set isClosed(value: boolean) {
		this._isClosed = value
		this.forceChangeDetection()
	}


	/**
	Area debug visualizzata
	*/
	private _isDebugExpanded: boolean = true;
	public get isDebugExpanded(): boolean {
		return this._isDebugExpanded
	};
	public set isDebugExpanded(value: boolean) {
		this._isDebugExpanded = value
		this.forceChangeDetection()
	}

	/**
	Stream in arrivo
	*/
	private _isIncoming: boolean = false;
	public get isIncoming(): boolean {
		return this._isIncoming
	};
	public set isIncoming(value: boolean) {
		this._isIncoming = value
		this.forceChangeDetection()
	}

	/**
	Audio in arrivo
	*/
	private _isIncomingAudio: boolean = false;
	public get isIncomingAudio(): boolean {
		return this._isIncomingAudio
	};
	public set isIncomingAudio(value: boolean) {
		this._isIncomingAudio = value
		this.forceChangeDetection()
	}

	/**
	Video in arrivo
	*/
	private _isIncomingVideo: boolean = false;
	public get isIncomingVideo(): boolean {
		return this._isIncomingVideo
	};
	public set isIncomingVideo(value: boolean) {
		this._isIncomingVideo = value
		this.forceChangeDetection()
	}

	/**
	Stream in uscita
	*/
	private _isOutgoing: boolean = false;
	public get isOutgoing(): boolean {
		return this._isOutgoing
	};
	public set isOutgoing(value: boolean) {
		this._isOutgoing = value
		this.forceChangeDetection()
	}

	/**
	Audio in uscita
	*/
	private _isOutgoingAudio: boolean = false;
	public get isOutgoingAudio(): boolean {
		return this._isOutgoingAudio
	};
	public set isOutgoingAudio(value: boolean) {
		this._isOutgoingAudio = value
		this.forceChangeDetection()
	}


	/**
	Video in uscita
	*/
	private _isOutgoingVideo: boolean = false;
	public get isOutgoingVideo(): boolean {
		return this._isOutgoingVideo
	};
	public set isOutgoingVideo(value: boolean) {
		this._isOutgoingVideo = value

		this.forceChangeDetection()
	}


	public isPlayingRingtone: boolean = false;

	/**
	Loader di inizio chiamata
	*/
	private loader: Loading

	/**
	Eventuale stream in arrivo
	*/
	private _inputStream: MediaStream
	public get inputStream(): MediaStream {
		return this._inputStream
	}
	/**
	Impostazione o sostituzione stream da remoto
	*/
	public set inputStream(inputStream: MediaStream) {
		let previousStream = this._inputStream
		this._inputStream = inputStream

		if (inputStream && previousStream && previousStream.id != inputStream.id) {
			console.warn('SOSTITUZIONE STREAM: NON IMPLEMENTATO')
		}

		if (inputStream) {
			let audioTracks = inputStream.getAudioTracks()
			console.debug(audioTracks.length + " Tracce audio in arrivo:", audioTracks)
			let videoTracks = inputStream.getVideoTracks()
			console.debug(videoTracks.length + " Tracce video in arrivo:", videoTracks)

			this.isIncoming = true
			this.isIncomingAudio = audioTracks.length > 0
			this.isIncomingVideo = videoTracks.length > 0

			if (this.isCaller) {//Il chiamante sta ricevendo risposta
				this.callStatus = CallStatus.CONNECTED
			}
		} else {
			console.debug('Input stream rimosso')
			this.isIncoming = false
			this.isIncomingAudio = false
			this.isIncomingVideo = false
		}

		//chiusura eventuale loader attivo
		this.closeLoader()

		//play input
		if (this.inputVideoElement) {
			let video = this.inputVideoElement.nativeElement
			video.volume = 1
			video.autoplay = true

			/*
			//funzione per verificare se è già avviato
			video.playing=function(){
			return !!(this.currentTime > 0 && !this.paused && !this.ended && this.readyState > 2);		};
			*/

			if ("srcObject" in video) {
				video.srcObject = inputStream
			} else {
				// Avoid using this in new browsers, as it is going away.
				video.src = window.URL.createObjectURL(inputStream)
			}

			let self = this

			video.tryPlay = function () {
				console.debug('[call.inputStream.set().tryPlay()] Avvio autoplay..')

				try {
					var played = video.play()
					if (played !== undefined) {
						played.then(() => {
							// Autoplay started!
							console.debug('[call.inputStream.set().tryPlay()] Autoplay iniziato')
							self.forceChangeDetection()
						}).catch(e => {
							console.warn("Errore durante l'autoplay, ritento una volta", e)
							// Autoplay was prevented.
							// Show a "Play" button so that user can start playback.
							video.play().then(() => {
								// Autoplay started!
								self.forceChangeDetection()
								console.debug('[call.inputStream.set().tryPlay()] Autoplay iniziato al secondo tentativo')
							}).catch(e => {
								//TODO provare a mettere un timer di X millisecondi per vedere se dando tempo si sblocca
								console.error("Impossibile eseguire il video in 2 tentativi", e)
							})
						})
					}
				} catch (e) {//browser senza promise nel play()
					console.warn("Eccezione durante l'autoplay, ritento una volta", e)
					// Autoplay was prevented.
					// Show a "Play" button so that user can start playback.
					try {
						video.play()
						self.forceChangeDetection()
						console.debug('[call.inputStream.set().tryPlay()] Autoplay iniziato al secondo tentativo con eccezione')
					} catch (e) {
						console.error("Impossibile eseguire il video in 2 tentativi con eccezione", e)
					}
				}
			}


			video.onloadedmetadata = (_) => {
				console.debug('[call.inputStream.set()] Metadati video caricati, play automatico..')
				video.tryPlay()
			}

			video.tryPlay()
			self.forceChangeDetection()
		} else {
			console.warn('Elemento video non disponibile')
		}
	}


	//public peerConnection: WebRTCConnection = null;
	private ringtoneId: string = null;
	private ringtoneInterval: number = null;

	private signalingErrorHandler: any = (error) => {
		console.warn('Errore di connessione al server di signaling, chiudo la chiamata', error)
		this.hangUp('Errore di connessione al server')
		//this.events.unsubscribe(this.signalingErrorHandler)
		this.events.unsubscribe(DomainEvents.ServerSignalingConnectionError, this.signalingErrorHandler)
	};


	constructor(
		private alertCtrl: AlertController,
		public auth: AuthProvider,
		private changeDetector: ChangeDetectorRef,
		private devices: DeviceProvider,
		private events: Events,
		//private loadingCtrl: LoadingController,
		private nativeAudio: NativeAudio,
		private signaling: SignalingSocketIoProvider,
		private toastCtrl: ToastController,
		//private toastCtrl: ToastController,
		private webrtc: WebrtcProvider,
	) {
		//let self = this;
		console.debug('[CallComponent.new()]')
	}

	public answer(video: boolean = false) {
		console.group('[CallComponent.answer()] Rispondo ' + (video ? 'CON' : 'SENZA') + ' video')

		//ferma suoneria e timer chiusura automatica
		this.stopRingtone()
		this.stopCallTimeout()
		this.callStatus = CallStatus.SENDING

		this.webrtc.answer(video)
		console.groupEnd()
	}

	/**
	Chiamata in arrivo: Presenta le U.I. per rispondere
	*/
	private askAnswer() {
		console.debug('[CallComponent.askAnswer()] Presento le U.I. per rispondere alla chiamata in arrivo')

		let self = this
		this.startRingtone()
		this.devices.moveToForeground()//porta in primo piano

		//console.debug('[CallComponent.askAnswer()] Timer chiusura chiamata automatica chiamata se non risponde..');
		self.startCallTimeout()

		/**
		Gestione chiamata dopo aver verificato i dispositivi disponibili
		*/
		console.debug('Controllo i dispositivi disponibili per rispondere..')
		this.devices.enumerateDevices().then((hasInput) => {
			console.group('[CallComponent.devices.enumeratedevices().then()] Dispositivi per rispondere verificati')

			if (!hasInput) {
				console.warn('Nessun dispositivo di input disponibile, impossibile rispondere')
				//TODO XXX Non uscire, è possibile aprire il portone senza rispondere
				self.handleNoMediaDeviceAvailable()
				self.stopCallTimeout()
				return
			}

			//Cambia stato
			self.callStatus = CallStatus.RINGING

			console.groupEnd()
		}).catch(e => {
			console.error('Errore elencando i dispositivi di input', e)
			self.hangUp('Un problema con il microfono o con la cam impedisce la chiamata')
		})
	}


	/**
	Inizia chiamata in uscita.

	1 verifica quali dispositivi di input sono disponibili

	2 se ne ha almeno uno invoca WebrtcProvider.call()
	*/
	private call(): Promise<void> {
		console.group('[CallComponent.call()] Avvio chiamata in uscita')
		let self = this

		return new Promise((resolve, reject) => {
			//aggiorna i dispositivi disponibili e avvia la chiamata
			self.devices.enumerateDevices().then((hasInput) => {
				console.debug('Dispositivi di input enumerati..')

				if (!hasInput) {
					console.debug('Nessun dispositivo di input disponibile.')
					let alert = self.alertCtrl.create({
						title: 'Impossibile chiamare',
						message: 'Nessun dispositivo di input disponibile per chiamare',
						buttons: [
							{
								text: 'Annulla',
								role: 'cancel',
								handler: () => {
									console.debug('Chiamata annullata per mancanza di dispositivi di input')
									self.hangUp('Nessun dispositivo di input disponibile')
								}
							},
						]
					})
					alert.present()
					resolve()
				} else {
					console.debug('Dispositivi di input disponibili, chiamo: ' + self.otherId)

					//se non risponde entro X secondi mette giù
					self.startCallTimeout()

					//Loader non visibile: può interagire mentre chiama
					//self.openLoader('Chiamo..')

					//inizializza e invia call_init
					self.callStatus = CallStatus.REQUESTING

					//aggiorna lo status al primo invio effettivo di stream
					self.webrtc.onSend.once((outputStream) => {
						self.callStatus = CallStatus.SENDING
						self.outputStream = outputStream

						if (outputStream) {
							let audioTracks = outputStream.getAudioTracks()
							console.debug(audioTracks.length + " Tracce audio in uscita:", audioTracks)
							let videoTracks = outputStream.getVideoTracks()
							console.debug(videoTracks.length + " Tracce video in uscita:", videoTracks)

							this.isOutgoing = true
							this.isOutgoingAudio = audioTracks.length > 0
							this.isOutgoingVideo = videoTracks.length > 0
						} else {
							console.debug('Input stream rimosso')
							this.isIncoming = false
							this.isIncomingAudio = false
							this.isIncomingVideo = false
						}
					})

					//invia call_init e registra l'invio stream quando riceve call_handled
					self.webrtc.call(self.otherId,
						self.devices.isVideoInputSupported,
						self.devices.isAudioInputSupported,
						self.housingUnit ? self.housingUnit.id : null
					).then(() => {
						resolve()
					}).catch(e => {
						console.error('Errore iniziando la chiamata P2P', e)
						self.hangUp('Errore iniziando la chiamata')
						reject()
					})
				}

				console.groupEnd()
			}).catch(e => {
				console.error('Errore elencando i dispositivi di input', e)
				self.hangUp('Impossibile elencare i dispositivi di input')

				console.groupEnd()
			})
		})
	}

	/**
	Chiude il loader
	*/
	private closeLoader() {
		if (this.loader) {
			this.loader.dismiss()
			this.loader = null
		}
	}

	/**
	Gestisce e presenta eventuali errori di devices.getUserMedia()
	*/
	/*
	private handleGetUserMediaError(error): void {
	let self = this;

	console.error('Errore recuperando i dispositivi di input per rispondere: ' + error.name + " - " + error.message, error);
	let message: string;
	switch (error.name) {
	default:
	message = 'Errore recuperando i dispositivi di input';
	break;
	case "NotAllowedError":
	message = "Per attivare le chiamate devi concedere a questa app l'accesso ai dispositivi";
	break;
	case "AbortError":
	message = "Impossibile accedere ad uno o più dispositivi di input";
	break;
	case "NotFoundError":
	message = "Impossibile trovare il tipo di dispositivo di input richiesto";
	break;
	case "NotReadableError":
	message = "Impossibile ricevere input dal dispositivo";
	break;
	case "OverconstrainedError":
	message = "Questo dispositivo non soddisfa i requisiti di streaming";
	break;
	case "SecurityError":
	message = "Il tuo sistema non consente l'accesso al dispositivo";
	break;
	case "TypeError":
	message = "Nessun dispositivo adeguato disponibile";
	break;
	}


	let alert = self.alertCtrl.create({
	title: 'Impossibile accedere ai dispositivi di input',
	message: 'Impossibile procedere con la chiamata: ' + message,
	buttons: [
	{
	text: 'Annulla',
	role: 'cancel',
	handler: () => {
	self.stopRingtone()//Questa schermata appare in attesa di risposta, quando ha gestito smette di suonare
	self.hangUp('Impossibile accedere ai dispositivi di input')
	}
	},
	]
	});
	alert.present();
	}
	*/

	private forceChangeDetection() {
		//XXX il tipo non esplicita il campo destroyed
		if (!this.changeDetector['destroyed']) {
			this.changeDetector.detectChanges()
		}
	}

	public handleNoMediaDeviceAvailable() {
		let self = this
		console.warn('Nessun dispostivo di input disponibile')
		let alert = self.alertCtrl.create({
			title: 'Impossibile rispondere',
			message: 'Chiamata in arrivo ma nessun dispositivo audio o video disponibile per rispondere',
			buttons: [
				{
					text: 'Rifiuta',
					role: 'cancel',
					handler: () => {
						self.hangUp('Impossibile rispondere alla chiamata.')
					}
				},
			]
		})
		alert.present()
	}

	/*
	Mette giù
	*/
	public hangUp(message: string = null, sendHangup: boolean = true) {
		console.debug('[CallComponent.hangUp(message="' + message + '",sendHangup=' + (sendHangup ? 'true' : 'false') + ')]: Fine chiamata')

		this.callStatus = CallStatus.DISCONNECTED

		if (!message) {
			message = 'Fine chiamata.'
		}
		let self = this

		console.group('[CallComponent.hangUp(message="' + message + '",sendHangup=' + sendHangup + ')] Finalizzazione chiamata')

		if (this.isClosed) {
			//ignora la richiesta, ha già messo giu
			console.debug('Richiesta di chiusura chiamata ignorata: Ha già messo giu, risegnalo alla pagina')
			this.events.publish(DomainEvents.VOIPCALL_CLOSE, message)
			//NOTE non funzionava, usato evento globale
			//this.onClose.emit(message)
			console.groupEnd()
			return
		}
		this.isClosed = true


		this.hangUpMessage = message

		/*
		Stop timer risposta
		*/
		console.debug('[CallComponent.hangUp()]: Stop dell\'eventuale timeout di risposta..')
		self.stopCallTimeout()

		/*
		Stop suoneria
		*/
		console.debug('[CallComponent.hangUp()]: Stop dell\'eventuale suoneria..')
		this.stopRingtone()

		//chiusura loader
		//console.debug('[CallComponent.hangUp()]: Chiusura dell\'eventuale loader..')
		this.closeLoader()

		/*
		Stop input
		*/
		//console.debug('Chiusura degli input..')
		this.isIncoming = false
		this.isIncomingAudio = false
		this.isIncomingVideo = false
		if (this.inputStream) {
			this.stopStream(this.inputStream)
			this.inputStream = null
		}

		if (this.outputStream) {
			this.stopStream(this.outputStream)
			this.outputStream = null
		}

		/*
		Disconnessione pulita
		*/
		//console.debug('Invio notifica di fine chiamata..')
		if (sendHangup) {
			this.webrtc.signaling.sendCallHangup(message)
		}

		this.webrtc.disconnect()

		this.unloadRingtone()

		console.debug('[CallComponent.hangUp()] Propagazione di fine chiamata alla pagina contenitore..')
		this.events.publish(DomainEvents.VOIPCALL_CLOSE, message)

		//NOTE non funzionava, usato evento globale
		//this.onClose.emit(message)

		console.groupEnd()
	}


	/**
	Se ha ricevuto tutti i parametri necessari, inizializza la connessione
	*/
	private init() {
		let self = this

		if (!this._otherId || this._isCaller === null) {
			console.debug('[CallComponent.init()] Componente non valorizzato, inizializzazione posticipata')
			return
		}

		console.debug('[CallComponent.init()] Impostazione parametri completata, inizializzazione della chiamata..')

		try {
			/*
			Chiamato e chiamante: Riceve lo stream remoto
			*/
			this.webrtc.onStream.once(inputStream => {
				console.debug('[CallComponent.webrtc.onStream.once()] Ricezione stream dal peer remoto:', inputStream, inputStream.getTracks())

				if (self._isCaller) {
					console.debug('Ricevuto stream di risposta dal chiamante')
					self.stopCallTimeout()
				}

				this.inputStream = inputStream
			})

			/*
			Chiamante: chiamata presa in gestione, aggiorno le UI
			*/
			if (this._isCaller) {
				self.webrtc.signaling.onCallHandled.once(() => {
					console.debug('[CallComponent.webrtc.signaling.onCallHandled.once()] Il chiamato ha preso in gestione la richiesta di chiamata')
					self.isConnected = true
					self.closeLoader()
					self.onConnected.emit()
				})
			}

			/**
			Eventi da sottoscrivere:
			- chiamata in arrivo
			- chiamata terminata
			*/
			this.events.subscribe(DomainEvents.ServerSignalingConnectionError, this.signalingErrorHandler)

			/*
			this.events.subscribe(DomainEvents.ServerSignalingConnectionFinalized, () => {
			console.debug('Connessione al server di signaling finalizzata, chiudo la chiamata');
			self.hangUp('Connessione al server interrotta');
		});
		*/


			this.webrtc.onClose.once(() => {
				console.debug("[CallComponent.webrtc.onClose.once()] Connessione chiusa")
				if (!self.isConnected) {
					self.hangUp('Chiamata non presa')
				} else {
					self.hangUp('Connessione chiusa')
				}
			})

			this.webrtc.onError.once(() => {
				console.warn("[CallComponent.webrtc.onError.once()] Errore di connessione")
				if (!self.isConnected) {
					self.hangUp('Impossibile chiamare in questo momento')
				} else {
					self.hangUp('Connessione persa')
				}
			})

			/*
			LISTENER SIGNALING SERVER
			*/

			/*
			listener server di signaling
			*/
			this.webrtc.signaling.onLogout.once(() => {
				console.info("[CallComponent.webrtc.signaling.onLogout.once()] Deregistrato dal server di signaling")
				self.hangUp('Disautenticato dal server')
			})

			this.webrtc.signaling.onOffline.once((_) => {
				console.info("[CallComponent.webrtc.signaling.onLogout.once()] Deregistrato dal server di signaling")
				self.hangUp('Connessione col server persa')
			})

			//ricevuto signal di notifica fine chiamata
			this.webrtc.signaling.onCallHangup.once(() => {
				console.info("[CallComponent.webrtc.signaling.onCallHangup.once()] Ricevuto signaling di chiusura chiamata")
				if (this.inputStream) {
					self.hangUp('Chiamata terminata', false)
				} else {
					self.hangUp('Il destinatario potrebbe avere il dispositivo spento o non raggiungibile', false)
				}
			})

			/**
			Inizializzato, _isCaller E this.otherId valorizzati, chiamata vera e propria
			*/
			if (this._isCaller) {
				console.debug('[CallComponent.init()] Caller: Avvio la richiesta di presa in gestione chiamata')
				this.call()//avvia chiamata in uscita
			} else {
				console.debug('[CallComponent.init()] Called: Ascolto per stream e conferma presa in gestione chiamata')
				this.webrtc.listen().then(() => {
					console.debug('Pronto a ricevere lo stream, invio notifica di chiamata presa in gestione')
					this.webrtc.signaling.sendCallHandled(this.otherId).then(() => {
						console.debug('Notifica di presa in gestione, possibile invio stream dal peer remoto, chiedo come rispondere..')
						this.askAnswer()
					})
				})
			}
		} catch (e) {
			console.error('Errore inizializzando CallComponent:', e)
		}
	}

	/**
	deve essere invocato esplicitamente dal contenitore
	*/
	public ionViewDidEnter() {
	}
	/**
	deve essere invocato esplicitamente dal contenitore
	*/
	public ionViewDidLeave() {
		console.debug('[CallComponent.ionViewDidLeave()] Pulizia: Fermo eventuale suoneria, chiusira chiamata, disconnessione webrtc, desottoscrizione eventi')

		this.stopRingtone()

		// evita di lasciare peer/stream vivi se l'utente esce dalla schermata
		if (!this.isClosed) {
			// non inviare hangup se non vuoi notificare, ma pulisci tutto
			this.hangUp('Chiamata terminata', false)
		} else {
			this.webrtc.disconnect()
		}

		// IMPORTANT: ionic Events unsubscribe richiede (eventName, handler)
		this.events.unsubscribe(DomainEvents.ServerSignalingConnectionError, this.signalingErrorHandler)

	}
	/**
	Peer id connesso
	*/
	public get myId(): string {
		return this.webrtc.peerId
	}

	/**
	Invia un messaggio al citofono chiedendogli di aprire il portone
	*/
	public openDoorway() {
		//signaling -> citofono -> hardware
		this.signaling.sendOpenDoorway(this.otherId).then(() => {
			let toast = this.toastCtrl.create({
				message: "Comando di apertura inviato",
				position: 'middle',
				duration: 3000,
				//cssClass: "light"
			})
			toast.present()
		}).catch(e => {
			console.error('Errore inviando la richiesta di apertura portone al dispositivo chiamante:', e)
			let toast = this.toastCtrl.create({
				message: "Problemas di rete: Impossibile aprire in questo momento",
				position: 'middle',
				duration: 3000,
				cssClass: "danger"
			})
			toast.present()
		})
	}


	/**
	Esegue 1 volta il play della suoneria impostata
	*/
	private playRingtone() {
		console.group('[CallComponent.playRingtone()] Avvio esecuzione suoneria: ' + this.ringtoneId)
		let self = this
		if (!self.ringtoneId) {
			console.warn('Si è tentato di eseguire la suoneria senza aver impostato un ringtoneId')
		}

		self.nativeAudio.play(self.ringtoneId).then(() => {
			console.debug('[CallComponent.playRingtone()] Play suoneria avviato: ' + self.ringtoneId)
		}).catch(error => {
			console.error('Errore avviando la suoneria:', self.ringtoneId, error)
			self.stopRingtone()
		})
		console.groupEnd()
	}

	/**
	Avvia il timer dopo il quale se il chiamato non ha risposto mette giù
	*/
	private startCallTimeout() {
		console.debug('[CallComponent.startCallTimeout()] Avvio il timer di chiusura chiamata se il chiamato non risponde..')
		let self = this

		self.callTimeout = setTimeout(
			() => {
				console.debug('Il chiamato non ha risposto in tempo, metto giù..')
				self.hangUp("Nessuna risposta.")
			},
			ConfigProvider.TIMEOUT_CALL_ANSWER * 1000
		)
	}


	/**
	Avvia la suoneria
	*/
	private startRingtone() {
		console.debug('[CallComponent.startRingtone()] Avvio la suoneria..')
		if (this.devices.isBrowser) {
			return
		}

		let self = this

		if (self.isPlayingRingtone && self.ringtoneInterval) {
			console.warn('Suoneria già accesa e avviata, non verrà riavviata')
		} else if (self.isPlayingRingtone || self.ringtoneInterval) {
			console.warn('Suoneria parzialmente attiva (solo accesa o solo avviata), forzo il riavvio della suoneria..')
			self.stopRingtone().then(() => {
				self.startRingtone()//riavvia dopo averla spenta
			})
		} else {
			console.debug('Suoneria spenta e non avviata, la avvio')

			if (self.ringtoneId) {
				console.debug('Suoneria già caricata, avvio..')
				self.startRingtoneLoop()
			} else {
				console.debug('Media suoneria non ancora caricato, eseguo il caricamento ed avvio la suoneria')
				this.ringtoneId = ConfigProvider.CALL_BUZZ_ID

				let volume = 1
				let voices = 1
				let delay = 1
				this.nativeAudio.preloadComplex(self.ringtoneId, ConfigProvider.CALL_BUZZ_URI, volume, voices, delay).then(() => {
					console.debug('Media suoneria caricato, avvio il loop della suoneria..')
					self.startRingtoneLoop()
				}).catch(error => {
					console.error('Errore precaricando la suoneria', error)
				})
			}
		}
	}


	/**
	Avvia il loop sella suoneria
	*/
	private startRingtoneLoop() {
		let self = this

		self.isPlayingRingtone = true

		let loopTime = 3500
		self.ringtoneInterval = setInterval(() => {
			console.group('[CallComponent.startRingtoneLoop()] Loop ringtone')
			if (self.isPlayingRingtone && self.callStatus != CallStatus.DISCONNECTED) {//se non deve suonare o non è più connesso si ferma
				console.debug('Suoneria attiva')
				self.playRingtone()
			} else {
				console.debug('Suoneria non più attiva')
				if (self.ringtoneInterval) {
					console.debug('Interval suoneria ancora attivo, lo interrompo')
					self.stopRingtone()
				} else {
					console.debug('Interval suoneria già interrotto, nessuna ulteriore azione')
				}
			}
			console.groupEnd()
		}, loopTime)

		//suona 1 volta subito
		self.playRingtone()
	}

	/**
	Se attivo, disattiva il timer per mettere giù se il chiamato non risponde in tempo
	*/
	private stopCallTimeout() {
		if (this.callTimeout) {
			console.debug('[CallComponent.stopCallTimeout()] Rimozione timeout di risposta')
			clearTimeout(this.callTimeout)
		}
	}

	/**
	Ferma la suoneria se sta suonando
	*/
	public stopRingtone(): Promise<void> {
		console.group('[CallComponent.stopRingtone()] Fermo la suoneria')
		if (this.isPlayingRingtone) {
			console.debug('Spengo la suoneria..')
			this.isPlayingRingtone = false
		} else {
			console.debug('Suoneria già spenta, isPlayingRingtone=false')
		}

		if (this.ringtoneInterval) {
			console.debug('Interrompo interval della suoneria..')
			clearInterval(this.ringtoneInterval)
			this.ringtoneInterval = null
		} else {
			console.debug('Interval suoneria già interrotto, ringtoneInterval=null')
		}

		let self = this
		if (this.ringtoneId) {
			return new Promise((resolve, _) => {
				self.nativeAudio.stop(this.ringtoneId).then(() => {//ferma
					self.unloadRingtone()//svuota buffer
					console.debug('[CalllComponent.stopRingtone()] Suoneria fermata.')
					console.groupEnd()
					resolve()
				}).catch(error => {
					console.error('Errore fermando la suoneria:', error)
					console.groupEnd()
					resolve()
				})
			})
		} else {
			console.debug('ID suoneria non impostata, impossibile fermarla')
			console.groupEnd()
		}
		return Promise.resolve()
	}

	private stopStream(stream: MediaStream) {
		console.group('[CallComponent.stopStream()] Fermo lo stream:', stream)
		try {
			//ferma e rimuove le tracce
			let tracks = stream.getTracks()
			for (let track of tracks) {
				if (track.stop) {
					console.debug('[CalllComponent.stopStream()] stream.track.stop()')
					track.stop()
				}
				console.debug('[CalllComponent.stopStream()] stream.removeTrack():', track)
				stream.removeTrack(track) //Da errore se chiamato su stream già fermo
			}

			//ferma lo stream se disponibile nel browser
			if ((stream as any).stop) {
				console.debug('[CalllComponent.stopStream()] stream.stop()', stream);
				(stream as any).stop()
			}
		} catch (e) {
			console.error('Errore fermando lo stream', e)
		}
		console.groupEnd()
	}

	/**
	Rimuove la suoneria dalla memoria
	*/
	private unloadRingtone() {
		if (this.ringtoneId) {
			this.nativeAudio.unload(this.ringtoneId)
			this.ringtoneId = null
		}
	}
}



// WEBPACK FOOTER //
// ./src/components/call/call.ts